import * as React from 'react';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import FormErrorMessage from '../../../components/ErrorMessage';
import DateTimeInput from '../../../components/DateTimeInput';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { IncidentFormTypes } from '../IncidentSubmit';

interface AddIndividualModalTypes {
	closeModal?: closeModalType;
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
}

const AddIndividualModal: React.FC<AddIndividualModalTypes> = ({
	closeModal,
	incidentFormData,
	setIncidentFormData
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const initialValues = {
		name: '',
		role: '',
		dateAndTime: '',
		contactMethod: '',
		isNotifiedSuccess: false
	};

	const validationSchema = Yup.object({
		name: Yup.string().required('Required'),
		role: Yup.string().required('Required'),
		dateAndTime: Yup.string().required('Required')
	});

	const submitHandler = (values: any) => {
		const formData = {
			name: values.name as string,
			role: values.role as string,
			dateAndTime: values.dateAndTime as string,
			contactMethod: values.contactMethod as string,
			isNotifiedSuccess: values.isNotifiedSuccess as boolean
		};

		const currentIndividuals = incidentFormData.individuals || [];

		const newIndividuals = [...currentIndividuals, formData];

		setIncidentFormData((prevState) => ({
			...prevState,

			individuals: newIndividuals
		}));

		if (closeModal) {
			closeModal();
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="form-group">
													<label htmlFor="name" className="label">
														Full Name
													</label>

													<div className="form-field">
														<Field name="name" id="name" type="text" className="form-input" />

														<ErrorMessage name="name" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="role" className="label">
														Role
													</label>

													<div className="form-field">
														<Field
															name="role"
															id="role"
															type="text"
															className="form-input"
															placeholder={'E.g. teacher, parent, supervisor...'}
														/>

														<ErrorMessage name="role" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label className="label">Date and time of contact</label>

													<DateTimeInput
														dateTimeString={formik.values.dateAndTime}
														onChange={(val: string) => formik.setFieldValue('dateAndTime', val)}
														excludeFuture
													/>
												</div>

												<div className="form-group">
													<label htmlFor="contactMethod" className="label">
														Method of contact
													</label>

													<div className="form-field">
														<Field
															name="contactMethod"
															id="contactMethod"
															type="text"
															className="form-input"
															placeholder="E.g. telephone, email, text message, in person…"
														/>

														<ErrorMessage name="contactMethod" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group mbtom-40">
													<label className="label">Were they successfully notified of the incident?</label>

													<div className="form-field radio-group">
														<div className="radio-row">
															<Field
																name="isNotifiedSuccess"
																id="isNotifiedSuccess"
																type="radio"
																value={true}
																checked={formik.values.isNotifiedSuccess === true}
																onChange={() => formik.setFieldValue('isNotifiedSuccess', true)}
															/>

															<span className="radio-option">Yes</span>
														</div>

														<div className="radio-row">
															<Field
																name="isNotifiedSuccess"
																id="isNotifiedSuccess"
																type="radio"
																value={false}
																checked={formik.values.isNotifiedSuccess === false}
																onChange={() => formik.setFieldValue('isNotifiedSuccess', false)}
															/>

															<span className="radio-option">No</span>
														</div>

														<ErrorMessage name="isNotifiedSuccess" component="div" className="error-message" />
													</div>
												</div>

												<Button className="btn-primary full-width" type="submit">
													Add to the list
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AddIndividualModal;
